import { useEffect, useState } from 'react';
import {
  CLASSIFICATION_REQUIRES_GROUP_MESSAGE,
  classificationRequiresGroup,
  type Classification,
  type DocumentRegistryEntry,
} from '@kennisbank/shared';
import { apiDelete, apiPatch } from '../api/client.js';
import { DownloadIcon, TagIcon, Trash2Icon, XIcon } from './icons.js';
import { documentsToCsv, runSequentially, type BulkResult } from '../lib/documents-bulk.js';

/**
 * kb_admin bulk actions for the Documents table: the sticky selection bar
 * plus the two confirmation dialogs. Each action goes through the same
 * per-document admin endpoints the inline cells use
 * (`PATCH` / `DELETE /api/v1/admin/knowledgebase/documents/:filename`), so
 * RBAC, the ingest lock, chunk propagation and audit rows all stay
 * server-side. Export is client-side only — it writes the metadata already
 * on screen, nothing new is fetched.
 */

const CLASSIFICATIONS: { value: Classification; hint: string }[] = [
  { value: 'public', hint: 'Anyone signed in. Safe to share outside OneNebula.' },
  { value: 'internal', hint: 'Anyone signed in. OneNebula employees only.' },
  { value: 'confidential', hint: 'Only members of the selected access groups.' },
  { value: 'restricted', hint: 'Only members of the selected access groups; highest tier.' },
];

const DELETE_CONFIRM_WORD = 'delete';
const FAILURE_PREVIEW = 8;

export function BulkActionBar({
  count,
  onClear,
  onReclassify,
  onDelete,
  onExport,
}: {
  count: number;
  onClear: () => void;
  onReclassify: () => void;
  onDelete: () => void;
  onExport: () => void;
}): JSX.Element {
  return (
    <div className="pointer-events-none sticky bottom-4 z-10 flex justify-center px-6 pb-2">
      <div className="pointer-events-auto flex items-center gap-3 rounded-md bg-nebula-primary px-3 py-2 text-white shadow-lg">
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear selection"
          className="rounded-md p-1 hover:bg-white/10"
        >
          <XIcon size={14} />
        </button>
        <span className="ov-numeric text-sm">{count} selected</span>
        <div className="mx-1 h-4 w-px bg-white/20" aria-hidden />
        <BarButton icon={<TagIcon size={14} />} label="Re-classify" onClick={onReclassify} />
        <BarButton icon={<DownloadIcon size={14} />} label="Export CSV" onClick={onExport} />
        <BarButton icon={<Trash2Icon size={14} />} label="Delete" onClick={onDelete} />
      </div>
    </div>
  );
}

function BarButton({
  icon,
  label,
  onClick,
}: {
  icon: JSX.Element;
  label: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium hover:bg-white/10"
    >
      {icon}
      {label}
    </button>
  );
}

/** Triggers a browser download of the selected rows' metadata as CSV. */
export function exportDocumentsCsv(docs: readonly DocumentRegistryEntry[]): void {
  // BOM so Excel opens UTF-8 titles (diacritics, dashes) correctly.
  const blob = new Blob(['\uFEFF', documentsToCsv(docs)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `documents-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick — revoking synchronously can cancel the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function adminDocPath(filename: string): string {
  return `/api/v1/admin/knowledgebase/documents/${encodeURIComponent(filename)}`;
}

function docLabel(d: DocumentRegistryEntry): string {
  return d.documentTitle || d.filename;
}

// ----- Re-classify -----------------------------------------------------------

export function BulkReclassifyModal({
  docs,
  availableAccessGroups,
  onClose,
  onFinished,
}: {
  docs: DocumentRegistryEntry[];
  availableAccessGroups: string[];
  onClose: () => void;
  /** Called once the run ends (with any outcome) so the page can refetch. */
  onFinished: (result: BulkResult<DocumentRegistryEntry>) => void;
}): JSX.Element {
  const [classification, setClassification] = useState<Classification>('internal');
  const [groups, setGroups] = useState<string[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<BulkResult<DocumentRegistryEntry> | null>(null);

  const running = progress !== null && result === null;
  const needsGroup = classificationRequiresGroup(classification);
  const canSubmit = !running && (!needsGroup || groups.length > 0);

  const toggleGroup = (g: string): void =>
    setGroups((cur) => (cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]));

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    // Only send accessGroups for the tiers that are gated by them; a
    // downgrade to public/internal leaves each doc's existing groups alone
    // (they no longer affect visibility).
    const patch = needsGroup ? { classification, accessGroups: groups } : { classification };
    setProgress({ done: 0, total: docs.length });
    const res = await runSequentially(
      docs,
      async (d) => {
        await apiPatch(adminDocPath(d.filename), patch);
      },
      (done, total) => setProgress({ done, total }),
    );
    setResult(res);
    onFinished(res);
  };

  return (
    <ModalShell
      title={`Re-classify ${docs.length} ${docs.length === 1 ? 'document' : 'documents'}`}
      titleId="bulk-reclassify-title"
      busy={running}
      onClose={onClose}
    >
      {result ? (
        <ResultSummary verb="Re-classified" result={result} onClose={onClose} />
      ) : (
        <>
          <p className="ov-body text-nebula-fg-3">
            Sets the classification on every selected document and all of its indexed chunks. Each
            change is recorded in the audit log.
          </p>
          <fieldset className="mt-4 space-y-2" disabled={running}>
            <legend className="ov-meta-upper mb-1">Classification</legend>
            {CLASSIFICATIONS.map((c) => (
              <label key={c.value} className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="bulk-classification"
                  checked={classification === c.value}
                  onChange={() => setClassification(c.value)}
                  className="mt-1 accent-nebula-primary"
                />
                <span>
                  <span className="font-semibold capitalize text-nebula-fg-1">{c.value}</span>
                  <span className="block text-xs text-nebula-fg-3">{c.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
          {needsGroup && (
            <fieldset className="mt-4" disabled={running}>
              <legend className="ov-meta-upper mb-1">Access groups</legend>
              {availableAccessGroups.length === 0 ? (
                <p className="rounded-md bg-nebula-warning-bg px-3 py-2 text-xs text-nebula-warning">
                  No access groups are configured, so documents cannot be made {classification}{' '}
                  without becoming invisible to everyone.
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-3">
                    {availableAccessGroups.map((g) => (
                      <label key={g} className="flex items-center gap-1.5 text-sm text-nebula-fg-1">
                        <input
                          type="checkbox"
                          checked={groups.includes(g)}
                          onChange={() => toggleGroup(g)}
                          className="accent-nebula-primary"
                        />
                        {g}
                      </label>
                    ))}
                  </div>
                  <p className="ov-meta mt-1">
                    Replaces each document&apos;s existing access groups.
                    {groups.length === 0 && ` ${CLASSIFICATION_REQUIRES_GROUP_MESSAGE}`}
                  </p>
                </>
              )}
            </fieldset>
          )}
          <ModalFooter
            onCancel={onClose}
            cancelDisabled={running}
            confirmLabel={
              running && progress ? `Applying ${progress.done}/${progress.total}…` : 'Apply'
            }
            confirmDisabled={!canSubmit}
            onConfirm={() => void submit()}
          />
        </>
      )}
    </ModalShell>
  );
}

// ----- Delete ----------------------------------------------------------------

export function BulkDeleteModal({
  docs,
  onClose,
  onFinished,
}: {
  docs: DocumentRegistryEntry[];
  onClose: () => void;
  onFinished: (result: BulkResult<DocumentRegistryEntry>) => void;
}): JSX.Element {
  const [typed, setTyped] = useState('');
  const [blockFromIngest, setBlockFromIngest] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<BulkResult<DocumentRegistryEntry> | null>(null);

  const running = progress !== null && result === null;
  const confirmed = typed.trim().toLowerCase() === DELETE_CONFIRM_WORD;
  // Only the Confluence sync honours blockedFromIngest today; code-repo
  // syncs re-add deleted files regardless, so say so instead of offering
  // a checkbox that would not stick.
  const hasConfluenceDocs = docs.some((d) => d.confluenceSourceId);
  // codeSourceId is on the wire (browse projection) but not yet in the shared schema.
  const hasCodeDocs = docs.some((d) => (d as { codeSourceId?: string }).codeSourceId);
  const totalChunks = docs.reduce((n, d) => n + (d.chunkCount ?? 0), 0);

  const submit = async (): Promise<void> => {
    if (!confirmed || running) return;
    setProgress({ done: 0, total: docs.length });
    const res = await runSequentially(
      docs,
      async (d) => {
        // The route's type-to-confirm contract expects the filename echoed
        // back; the bulk dialog's own confirmation step is the human gate.
        await apiDelete(adminDocPath(d.filename), {
          confirmFilename: d.filename,
          blockFromIngest,
        });
      },
      (done, total) => setProgress({ done, total }),
    );
    setResult(res);
    onFinished(res);
  };

  return (
    <ModalShell
      title={`Delete ${docs.length} ${docs.length === 1 ? 'document' : 'documents'}`}
      titleId="bulk-delete-title"
      busy={running}
      onClose={onClose}
    >
      {result ? (
        <ResultSummary verb="Deleted" result={result} onClose={onClose} />
      ) : (
        <>
          <p className="ov-body text-nebula-fg-3">
            Removes {totalChunks} indexed {totalChunks === 1 ? 'chunk' : 'chunks'} from the
            Knowledge Agent and hides these documents from this list. This cannot be undone from
            here.
          </p>
          <ul className="mt-3 max-h-40 space-y-0.5 overflow-y-auto rounded-md border border-nebula-border-soft bg-nebula-gray-6 px-3 py-2 text-xs text-nebula-fg-2">
            {docs.map((d) => (
              <li key={d.filename} className="truncate" title={d.filename}>
                {docLabel(d)}
              </li>
            ))}
          </ul>
          {hasConfluenceDocs && (
            <label className="mt-3 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={blockFromIngest}
                onChange={(e) => setBlockFromIngest(e.target.checked)}
                disabled={running}
                className="mt-1 accent-nebula-primary"
              />
              <span>
                <span className="font-semibold text-nebula-fg-1">Block from re-ingest</span>
                <span className="block text-xs text-nebula-fg-3">
                  Some of these pages come from a Confluence sync. Without this, the next sync adds
                  them back.
                </span>
              </span>
            </label>
          )}
          {hasCodeDocs && (
            <p className="ov-meta mt-3">
              Files from a code repository sync come back on the next sync unless they are removed
              from the repository or the source.
            </p>
          )}
          <label className="mt-4 block text-sm text-nebula-fg-2">
            Type <code className="rounded bg-nebula-gray-5 px-1 font-mono text-xs">delete</code> to
            confirm
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submit();
              }}
              disabled={running}
              autoFocus
              className="mt-1 w-full rounded-md border border-nebula-border px-3 py-2 text-sm focus:border-nebula-secondary focus:outline-none"
            />
          </label>
          <ModalFooter
            onCancel={onClose}
            cancelDisabled={running}
            confirmLabel={
              running && progress ? `Deleting ${progress.done}/${progress.total}…` : 'Delete'
            }
            confirmDisabled={!confirmed || running}
            destructive
            onConfirm={() => void submit()}
          />
        </>
      )}
    </ModalShell>
  );
}

// ----- Shared dialog pieces --------------------------------------------------

function ModalShell({
  title,
  titleId,
  busy,
  onClose,
  children,
}: {
  title: string;
  titleId: string;
  busy: boolean;
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // Closing mid-run would hide progress while requests keep going.
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-nebula-fg-1/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg bg-nebula-surface p-6 shadow-lg">
        <h2 id={titleId} className="text-lg font-bold text-nebula-fg-1">
          {title}
        </h2>
        <div className="mt-2">{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({
  onCancel,
  cancelDisabled,
  confirmLabel,
  confirmDisabled,
  destructive = false,
  onConfirm,
}: {
  onCancel: () => void;
  cancelDisabled: boolean;
  confirmLabel: string;
  confirmDisabled: boolean;
  destructive?: boolean;
  onConfirm: () => void;
}): JSX.Element {
  return (
    <div className="mt-6 flex justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        disabled={cancelDisabled}
        className="rounded-md border border-nebula-border px-3 py-2 text-sm font-semibold text-nebula-fg-3 hover:bg-nebula-tint disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={confirmDisabled}
        className={`rounded-md px-4 py-2 font-ui text-sm text-white disabled:opacity-50 ${
          destructive
            ? 'bg-nebula-error hover:bg-nebula-error/90'
            : 'bg-nebula-primary hover:bg-nebula-secondary'
        }`}
      >
        {confirmLabel}
      </button>
    </div>
  );
}

function ResultSummary({
  verb,
  result,
  onClose,
}: {
  verb: string;
  result: BulkResult<DocumentRegistryEntry>;
  onClose: () => void;
}): JSX.Element {
  const ok = result.succeeded.length;
  const failed = result.failed;
  return (
    <div>
      {ok > 0 && (
        <p className="rounded-md bg-nebula-success-bg px-3 py-2 text-sm text-nebula-success">
          {verb} {ok} {ok === 1 ? 'document' : 'documents'}.
        </p>
      )}
      {failed.length > 0 && (
        <div className="mt-2 rounded-md bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
          <p className="font-semibold">
            {failed.length} {failed.length === 1 ? 'document' : 'documents'} failed. They stay
            selected so you can retry.
          </p>
          <ul className="mt-1 space-y-1 text-xs">
            {failed.slice(0, FAILURE_PREVIEW).map((f) => (
              <li key={f.item.filename}>
                <span className="font-semibold">{docLabel(f.item)}</span>: {f.message}
              </li>
            ))}
            {failed.length > FAILURE_PREVIEW && (
              <li>…and {failed.length - FAILURE_PREVIEW} more.</li>
            )}
          </ul>
        </div>
      )}
      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-nebula-primary px-4 py-2 font-ui text-sm text-white hover:bg-nebula-secondary"
        >
          Close
        </button>
      </div>
    </div>
  );
}
